import concatMapboxStyles from '../../mapbox/concatMapboxStyles';
import dedupeMapboxStyleSources from '../../mapbox/dedupeMapboxStyleSources';
import surfaceMapboxStylesLabels from '../../mapbox/surfaceMapboxStylesLabels';
import {compact, every, unzip, find, fromPairs} from 'lodash';
import mapboxgl from 'mapbox-gl';
import getAreasMapboxStyle from './getAreasMapboxStyle';
import getTracksMapboxStyle from './getTracksMapboxStyle';
import getPublicTracksMapboxStyle from './getPublicTracksMapboxStyle';
import getHighlightLocationMapboxStyle from './getHighlightLocationMapboxStyle';
import getHighlightGeometryMapboxStyle from './getHighlightGeometryMapboxStyle';
import getDemoPositionMapboxStyle from './getDemoPositionMapboxStyle';
import getWaypointsMapboxStyle from './getWaypointsMapboxStyle';
import fetchLayerMapboxStyle from './fetchLayerMapboxStyle';
import {GetSyncPromise, SyncPromise} from '../../react/useGetSyncPromise';
import getGridMapboxStyle from './getGridMapboxStyle';
import fetchUser from '../fetchUser';
import resolveMapboxURL from '../../mapbox/resolveMapboxURL';
import getTransformRequest from '../getTransformRequest';
import transformMapboxStyleFonts from '../../mapbox/transformMapboxStyleFonts';

interface GetSyncMapboxStyleOptions {
    areas: {[id: string]: Area};
    bounds?: mapboxgl.LngLatBounds;
    foldersMembership: MapFoldersMembership;
    grid: Grid;
    highlight: MapHighlight;
    highlightLocation?: mapboxgl.LngLatLike;
    interaction: MapInteraction;
    isPrinting: boolean;
    layers?: Layer[];
    layerTypes: {[key: string]: LayerType} | undefined;
    preview?: boolean;
    project: MapProject;
    searchResult: SearchResult | undefined;
    showAreas: boolean;
    showPublicTracks: boolean;
    showTracks: boolean;
    showRoutes: boolean;
    showWaypointLabels?: boolean;
    showWaypoints: boolean;
    tracks: {[id: string]: Track};
    waypoints: {[id: string]: Waypoint};
    zoom?: number;
    getSyncPromise: GetSyncPromise;
}

interface GetSyncMapboxStyleOutput {
    mapboxStyle: GaiaMapboxStyle;
    transformRequest: mapboxgl.TransformRequestFunction;
}

export default function getSyncMapboxStyle({
    areas,
    bounds,
    foldersMembership,
    grid,
    highlight,
    highlightLocation,
    interaction,
    isPrinting,
    layers = [],
    layerTypes,
    preview,
    project,
    searchResult,
    showPublicTracks,
    showAreas,
    showTracks,
    showRoutes,
    showWaypointLabels,
    showWaypoints,
    tracks,
    waypoints,
    getSyncPromise,
    zoom,
}: GetSyncMapboxStyleOptions): SyncPromise<GetSyncMapboxStyleOutput> {
    const [user] = getSyncPromise(fetchUser());

    const [styles, errors, isLoaded] = (!layerTypes ?
        [[], [], []] :
        unzip(compact([
            getDemoPositionMapboxStyle(layerTypes, layers),
            fetchLayerMapboxStyle(layerTypes, {
                opacity: 1,
                type: user && user.unitSystem === 'metric' ?
                    'GaiaTopoRasterMeters' :
                    'GaiaTopoRasterFeet',
            }, {preview, uniqueTilecacheKey: 'basemap'}),
            ...layers.map((layer: Layer) => fetchLayerMapboxStyle(
                layerTypes,
                layer,
                {preview}
            )),
            getHighlightGeometryMapboxStyle(highlight),
            getGridMapboxStyle(grid, bounds, zoom),
            getTracksMapboxStyle({
                showTracks,
                showRoutes,
                tracks,
                project,
                interaction,
                getSyncPromise,
                highlight,
                foldersMembership,
                isPrinting,
                user,
            }),
            getPublicTracksMapboxStyle({showPublicTracks, highlight, interaction}),
            getHighlightLocationMapboxStyle(highlightLocation),
            getWaypointsMapboxStyle({
                showWaypoints,
                showWaypointLabels,
                waypoints,
                interaction,
                highlight,
                searchResult,
                isPrinting,
                foldersMembership,
                user,
            }),
            getAreasMapboxStyle({
                showAreas,
                areas,
                interaction,
                highlight,
                isPrinting,
                foldersMembership,
                user,
            }),
        ]).map(promise => getSyncPromise(promise)))
    ) as any as [(GaiaMapboxStyle | undefined)[], (Error | undefined)[], boolean[]];

    const transformedStyles = styles.map(style => {
        if (!style) return undefined;
        return transformMapboxStyleFonts(
            style,
            (fontsArray: string[]) => {
                return [`${style.metadata.uniqueTilecacheKey}__${fontsArray.join(',')}`];
            },
        );
    });

    const glyphMap = fromPairs(compact(
        transformedStyles.map(style => {
            if (style && style.metadata && style.metadata.uniqueTilecacheKey && style.glyphs) {
                return [style.metadata.uniqueTilecacheKey, resolveMapboxURL(style.glyphs)];
            }
            return undefined;
        })
    ));

    const transformRequest = getTransformRequest(glyphMap);

    const mapboxStyle = {
        // This is a workaround for a bug in Mapbox GL JS. Calling the "addImage" method without
        // having "sprite" set throws an error.
        sprite: 'https://static.gaiagps.com/GaiaTopoGL/sprites-2019-04-12-10.11',

        ...dedupeMapboxStyleSources(
            concatMapboxStyles(
                surfaceMapboxStylesLabels(
                    compact(transformedStyles)
                )
            )
        ),
        glyphs: 'g://{fontstack}/{range}',
    };

    const defaultOutput = {mapboxStyle: {version: 8, layers: [], sources: {}}, transformRequest};

    return !layerTypes ? [defaultOutput, undefined, false] : [
        {mapboxStyle, transformRequest},
        find(errors),
        Boolean(every(isLoaded) && layerTypes && user)
    ];
}
